import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Check } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useDemo } from "@/store/DemoStore";
import { cn } from "@/lib/utils";
import { pushSupported, enablePush, disablePush } from "@/lib/push";
import { api } from "@/lib/api";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export function useMasterToggle(enabled) {
  const { refresh } = useDemo();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) await disablePush();
      else await enablePush();
      await refresh();
      toast.success(enabled ? "Notifications turned off on this device" : "Notifications enabled");
    } catch (e) {
      toast.error(e.message || "Couldn't update notifications");
    } finally {
      setBusy(false);
    }
  };
  return { busy, toggle, supported: pushSupported() };
}

export function PushMasterToggle({ enabled }) {
  const { busy, toggle, supported } = useMasterToggle(enabled);
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success("Test notification sent", { description: `Delivered to ${data.sent} device${data.sent > 1 ? "s" : ""}. On iPhone, background the app and check your lock screen.` });
      else if (!data.enabled) toast.error("Turn the dial on first, then try again.");
      else if (!data.subscriptions) toast.error("No device registered yet — toggle the dial off and on, then retry.");
      else toast.message("Sent to the push service", { description: "If no pop-up appears, allow notifications for this app in your device Settings." });
    } catch {
      toast.error("Couldn't send a test notification.");
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="py-3.5">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-navy-900">Push Notifications</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {supported ? "Real-time alerts on this device, even when the app is closed." : "Not supported on this browser."}
          </p>
        </div>
        <Switch data-testid="push-master-switch" disabled={busy || !supported} checked={!!enabled} onCheckedChange={toggle} />
      </div>
      {enabled && supported && (
        <button onClick={sendTest} disabled={testing} data-testid="push-test-btn" className="mt-2.5 text-xs font-semibold text-navy-700 underline underline-offset-2 transition-opacity disabled:opacity-50">
          {testing ? "Sending…" : "Send a test notification"}
        </button>
      )}
    </div>
  );
}

export default function NotificationBell({ enabled, dark = false }) {
  const { notifications = [], markNotificationsRead } = useDemo();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = notifications.filter((n) => !n.read).length;

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread) markNotificationsRead();
  };
  const go = (n) => {
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          aria-label="Notifications"
          className={cn(
            "relative grid h-10 w-10 place-items-center rounded-full transition-colors",
            dark ? "text-white/80 hover:bg-white/10 hover:text-white" : "text-navy-700 hover:bg-slate-100"
          )}
        >
          <Bell className="h-5 w-5" strokeWidth={1.9} />
          {unread > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-sky-500 px-1 text-[9px] font-bold text-white ring-2 ring-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-semibold text-navy-900">Notifications</p>
          {unread > 0 && <span className="text-xs text-slate-400">{unread} new</span>}
        </div>
        <div className="border-b border-slate-100 px-4">
          <PushMasterToggle enabled={enabled} />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <BellOff className="h-6 w-6 text-slate-300" />
              <p className="text-sm text-slate-400">You're all caught up.</p>
            </div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => go(n)}
                data-testid="notification-item"
                className={cn(
                  "flex w-full gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50",
                  !n.read && "bg-sky-50/60"
                )}
              >
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-sky-500")} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-navy-900">{n.title}</span>
                  {n.body && <span className="mt-0.5 block text-xs text-slate-500 line-clamp-2">{n.body}</span>}
                  <span className="mt-1 block text-[11px] text-slate-400">{timeAgo(n.at)}</span>
                </span>
              </button>
            ))
          )}
        </div>
        {notifications.length > 0 && unread === 0 && (
          <div className="flex items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
            <Check className="h-3 w-3" /> All read
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
